import { Button } from "@/components/ui/baseUi/button"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { word } from "@modules/core/utils/i18n"
import { getFormatedDateTime } from "@modules/language/languageUtils"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleAthleteSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { FakeSubscriptionButton } from "@modules/programs/components/programSubscriptionActions"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { CreditCard, LoaderCircle } from "lucide-react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramBundleSubscriptionActionsProps = { programBundle: ProgramBundleEntity }

export function ProgramBundleSubscriptionActions({ programBundle }: ProgramBundleSubscriptionActionsProps) {
  const {
    isUpdating: subscriptionLoading,
    subscribe,
    unsubscribe,
    programBundleStripeSubscriptionInfos,
    openPaymentSettings,
  } = useProgramBundleAthleteSubscription(programBundle)

  function InactiveSubscriptionButton() {
    return (
      <Button
        isLoading={subscriptionLoading}
        variant="primary"
        size="sm"
        disabled={subscriptionLoading}
        onClick={() => subscribe()}
        className="px-4"
      >
        {subscriptionLoading && <LoaderCircle className="animate-spin" />}
        {programBundle.type === ProgramBundleType.ON_OFF
          ? programBundle.price
            ? word("programBundle.onOff.subscription.buy.title")
            : word("programBundle.onOff.subscription.buy.free.title")
          : programBundle.price
            ? word("programBundle.subscription.paying.subscribe.title")
            : word("programBundle.subscription.free.subscribe.title")}
      </Button>
    )
  }

  if (!programBundleStripeSubscriptionInfos) {
    return <InactiveSubscriptionButton />
  }

  switch (programBundleStripeSubscriptionInfos.subscriptionStatus) {
    case ProgramSubscriptionStatus.ACTIVE:
      if (programBundle.type === ProgramBundleType.ON_OFF && programBundleStripeSubscriptionInfos.paidPrice?.value)
        return (
          <Text className="flex flex-row items-center gap-2 self-start text-textDefault-caption" fontStyle="TS">
            <CheckmarkIcon size={24} color="currentColor" className="text-neutral-700" />
            {word("program.onOff.paidOn", {
              paymentDate: getFormatedDateTime(programBundleStripeSubscriptionInfos.subscriptionDate),
            })}
          </Text>
        )
      else {
        const shouldDisplayManagePaymentSettings =
          programBundle.type === ProgramBundleType.VIRTUAL && programBundleStripeSubscriptionInfos.paidPrice?.value

        return (
          <div className="mt-2 flex flex-row items-center justify-between gap-4">
            {shouldDisplayManagePaymentSettings && (
              <Button
                variant="underline"
                onClick={() => openPaymentSettings()}
                className="grow"
                disabled={subscriptionLoading}
              >
                {word("programBundle.details.managePaymentSettings")}
              </Button>
            )}
            <Button
              variant="underline"
              onClick={() => unsubscribe()}
              disabled={subscriptionLoading}
              className={shouldDisplayManagePaymentSettings ? "grow" : ""}
            >
              {programBundle.type === ProgramBundleType.ON_OFF
                ? word("program.onOff.subscription.remove.title")
                : word("program.subscription.paying.unsubscribe.confirm.button")}
            </Button>
          </div>
        )
      }
    case ProgramSubscriptionStatus.PENDING_PAYMENT:
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center justify-center gap-2 rounded-2xl bg-neutral-900 p-2">
            <CreditCard color="currentColor" className="text-textOnColor-heading" />
            <Text fontStyle="LS" className="text-textOnColor-heading">
              {word("program.subscription.pending.payment.title")}
            </Text>
          </div>

          <div className="flex w-full flex-row justify-between">
            <Button variant="primary" onClick={() => subscribe()} disabled={subscriptionLoading}>
              {word("program.subscription.paying.proceed.title")}
            </Button>
            <Button variant="underline" onClick={() => unsubscribe()} disabled={subscriptionLoading}>
              {word("program.subscription.paying.cancel.title")}
            </Button>
          </div>
        </div>
      )
    case ProgramSubscriptionStatus.TRIALING:
      return (
        <div className="flex flex-col gap-2">
          {programBundleStripeSubscriptionInfos.trial && (
            <Text>
              {word("program.subscription.trialing.progression", {
                trialEndDate: getFormatedDateTime(programBundleStripeSubscriptionInfos.trial.endDate),
              })}
            </Text>
          )}
          <Button variant="underline" onClick={() => unsubscribe()} disabled={subscriptionLoading}>
            {word("program.subscription.trialing.cancel.button")}
          </Button>
        </div>
      )
    case ProgramSubscriptionStatus.PENDING_CANCELLATION: // only for paying subscription
      return <FakeSubscriptionButton text={word("program.subscription.paying.unsubscription_in_progress.title")} />
    case ProgramSubscriptionStatus.INACTIVE:
      return <InactiveSubscriptionButton />
  }
}
